import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, LayoutGrid, Palette, Users, 
  Shield, Plus, Check, Info, Lock
} from 'lucide-react';
// 💡 Corrected relative pathing for shared UI components
import { Button } from '../../../components/ui/Button';
// 🛰️ Updated import to point to unified shared types
import { CustomCalendar } from '@/types';

interface CustomCalendarCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (calendar: Partial<CustomCalendar>, targetScope: 'personal' | 'household') => void;
  currentUserId: string;
  currentHouseholdId: string | null;
}

interface CalendarColor {
  name: string;
  hex: string;
}

const CALENDAR_COLORS: CalendarColor[] = [
  { name: 'Sky', hex: '#0ea5e9' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Slate', hex: '#475569' },
];

export const CustomCalendarCreator: React.FC<CustomCalendarCreatorProps> = ({ 
  isOpen, onClose, onSave, currentUserId, currentHouseholdId 
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<Partial<CustomCalendar>>({
    title: '',
    color: CALENDAR_COLORS[0].hex,
    isVisible: true,
  });

  // 🛡️ Default to private if no household, otherwise default to shared
  const [scope, setScope] = useState<'personal' | 'household'>(
    currentHouseholdId ? 'household' : 'personal'
  );

  // Sync scope if household status changes while modal is open
  useEffect(() => {
    if (!currentHouseholdId) {
      setScope('personal');
    }
  }, [currentHouseholdId]);

  const handleSave = () => {
    if (!formData.title) return;
    
    /**
     * 🛰️ HYBRID ROUTING LOGIC
     * targetScope determines if the listener writes to /users/{uid} or /households/{hid}
     */
    onSave({
      ...formData,
      id: `cal-${Math.random().toString(36).substring(2, 9)}`,
      ownerId: scope === 'personal' ? currentUserId : (currentHouseholdId || currentUserId),
      collectionName: formData.title.toLowerCase().replace(/\s+/g, '_'),
    }, scope);
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-950 w-full max-w-md rounded-[3.5rem] shadow-2xl border border-slate-100 dark:border-slate-800 p-10 space-y-8 relative overflow-hidden">
        
        {/* Decorative Background Icon */}
        <LayoutGrid className="absolute -top-6 -right-6 w-32 h-32 text-slate-100 dark:text-slate-900 -z-10" />

        <header className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
              {t('cal.create_layer', 'New Layer')}
            </h2>
            <p className="text-2xs font-black text-brand uppercase tracking-[0.3em] mt-2">
              {t('cal.custom_registry', 'Custom Temporal Registry')}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-300" />
          </button>
        </header>

        <div className="space-y-6">
          {/* Name Input */}
          <div className="space-y-2">
            <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest">Calendar Name</label>
            <input 
              autoFocus
              placeholder="e.g. Renovation Schedule"
              value={formData.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, title: e.target.value })}
              className="w-full p-5 bg-slate-50 dark:bg-slate-900 rounded-[2rem] border-none font-bold text-lg outline-none focus:ring-2 ring-brand/20"
            />
          </div>

          {/* Scope Toggle - Hybrid Scoping Enforcement */}
          <div className="space-y-2">
            <div className="flex justify-between items-center ml-4 pr-4">
              <label className="text-2xs font-black uppercase text-slate-400 tracking-widest">Accessibility</label>
              {!currentHouseholdId && (
                <div className="flex items-center gap-1.5">
                  <Lock className="w-2.5 h-2.5 text-amber-500" />
                  <span className="text-[8px] font-black uppercase text-amber-500 tracking-tighter">Household Required for Sharing</span>
                </div>
              )}
            </div>
            <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl gap-1">
              <button
                type="button"
                disabled={!currentHouseholdId}
                onClick={() => setScope('household')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${scope === 'household' ? 'bg-white dark:bg-slate-800 shadow-md text-brand' : 'text-slate-400'} disabled:opacity-30 disabled:cursor-not-allowed`}
              >
                <Users className="w-3 h-3" /> Shared
              </button>
              <button
                type="button"
                onClick={() => setScope('personal')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${scope === 'personal' ? 'bg-white dark:bg-slate-800 shadow-md text-brand' : 'text-slate-400'}`}
              >
                <Shield className="w-3 h-3" /> Private
              </button>
            </div>
          </div>

          {/* Color Palette */}
          <div className="space-y-4">
            <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
              <Palette className="w-3 h-3" /> Theme Color
            </label>
            <div className="flex justify-between px-2">
              {CALENDAR_COLORS.map(c => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: c.hex })}
                  className={`w-10 h-10 rounded-full transition-all flex items-center justify-center ${formData.color === c.hex ? 'ring-2 ring-offset-4 ring-brand scale-110' : 'opacity-40 hover:opacity-100'}`}
                  style={{ backgroundColor: c.hex }}
                >
                  {formData.color === c.hex && <Check className="w-4 h-4 text-white" />}
                </button>
              ))}
            </div>
          </div>

          {/* Intelligence Note - Dynamic Privacy Feedback */}
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl flex items-start gap-3">
            <Info className="w-4 h-4 text-brand mt-0.5" />
            <p className="text-2xs font-bold text-indigo-800 dark:text-indigo-300 leading-relaxed uppercase tracking-tight">
              {scope === 'household' 
                ? "This layer will be created in the shared household partition. All members will see events added to this registry."
                : "This is a personal layer stored in your private partition. No other users can see these events, following Google Privacy verified protocols."
              }
            </p>
          </div>
        </div>

        <footer className="pt-4">
          <Button 
            onClick={handleSave}
            disabled={!formData.title}
            className="w-full h-16 rounded-[2rem] bg-brand hover:bg-brand-hover text-white font-black text-sm gap-3 shadow-xl shadow-brand/30 transition-all uppercase tracking-widest"
          >
            <Plus className="w-5 h-5" /> Create Registry
          </Button>
        </footer>
      </div>
    </div>
  );
};